import React from "react";
import {
  AlertTriangle,
  CheckCircle,
  FileText,
  Link2,
  Loader2,
  Upload,
  X,
} from "lucide-react";
import type { ValidateResponse } from "../../../../../api/frameworksApi";
import {
  FormField,
  FormInput,
  Modal,
} from "../../../../components/Modal";

import type { SspImportStep } from "../../types";
export type { SspImportStep };

export interface ImportSspDialogProps {
  open: boolean;
  onClose: () => void;
  resetSspImport: () => void;
  sspImportStep: SspImportStep;
  setSspImportStep: React.Dispatch<React.SetStateAction<SspImportStep>>;
  sspImportFile: File | null;
  setSspImportFile: React.Dispatch<React.SetStateAction<File | null>>;
  sspImportUrl: string;
  setSspImportUrl: React.Dispatch<React.SetStateAction<string>>;
  sspImportMode: "full" | "delta";
  setSspImportMode: React.Dispatch<React.SetStateAction<"full" | "delta">>;
  sspValidateResult: ValidateResponse | null;
  setSspValidateResult: React.Dispatch<
    React.SetStateAction<ValidateResponse | null>
  >;
  sspValidating: boolean;
  setSspValidating: React.Dispatch<React.SetStateAction<boolean>>;
  sspImportError: string | null;
  setSspImportError: React.Dispatch<React.SetStateAction<string | null>>;
  sspImportErrorTitle: string | null;
  sspRunImport: () => void;
  sspStartValidation: (file: File) => void;
  sspFetchUrl: () => void;
}

export function ImportSspDialog({
  open,
  onClose,
  resetSspImport,
  sspImportStep,
  setSspImportStep,
  sspImportFile,
  setSspImportFile,
  sspImportUrl,
  setSspImportUrl,
  sspValidateResult,
  setSspValidateResult,
  sspValidating,
  setSspValidating,
  sspImportError,
  setSspImportError,
  sspImportErrorTitle,
  sspRunImport,
  sspStartValidation,
  sspFetchUrl,
}: ImportSspDialogProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Import System Security Plan"
      description="Upload an OSCAL SSP document (.json, .xml, .yaml)"
      size="md"
      footer={
        sspImportStep === "select" ? (
          <div className="flex justify-end w-full gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-muted text-sm font-medium hover:bg-accent transition-colors"
            >
              Cancel
            </button>
            {sspImportFile && (
              <button
                onClick={sspRunImport}
                disabled={!sspValidateResult || sspValidating}
                className={`px-4 py-2 rounded-lg text-white text-sm font-bold transition-colors shadow-md ${!sspValidateResult || sspValidating
                  ? "bg-indigo-400 dark:bg-indigo-400 opacity-50 cursor-not-allowed"
                  : "bg-indigo-600 dark:bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-700"
                  }`}
              >
                Import
              </button>
            )}
          </div>
        ) : sspImportStep === "url" ? (
          <div className="flex justify-end w-full gap-2">
            <button
              onClick={() => {
                setSspImportStep("select");
                setSspImportUrl("");
                setSspImportFile(null);
                setSspValidateResult(null);
                setSspImportError(null);
                setSspValidating(false);
              }}
              className="px-4 py-2 rounded-lg bg-muted text-sm font-medium hover:bg-accent transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={sspRunImport}
              disabled={!sspValidateResult || sspValidating}
              className={`px-4 py-2 rounded-lg text-white text-sm font-bold transition-colors shadow-md ${!sspValidateResult || sspValidating
                ? "bg-indigo-400 dark:bg-indigo-400 opacity-50 cursor-not-allowed"
                : "bg-indigo-600 dark:bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-700"
                }`}
            >
              Import
            </button>
          </div>
        ) : sspImportStep === "findings" ? (
          <div className="flex items-center justify-end w-full gap-3">
            <button
              onClick={onClose}
              className="px-3 py-1.5 rounded-md border border-border text-sm hover:bg-accent transition-colors"
            >
              Close
            </button>
            <button
              onClick={resetSspImport}
              className="flex items-center gap-2 px-4 py-1.5 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity"
            >
              Try Again
            </button>
          </div>
        ) : null
      }
    >
      {/* Step: Select source */}
      {sspImportStep === "select" && (
        <div className="py-6 flex flex-col items-center gap-6">
          <input
            id="ssp-import-file-input"
            type="file"
            accept=".json,.yaml,.yml,.xml"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                setSspImportFile(file);
                setSspValidateResult(null);
                setSspImportError(null);
                sspStartValidation(file);
              }
            }}
          />
          {/* Import mode is always "full" until Delta ships, so the picker is
              not shown. */}
          <p className="text-sm font-semibold text-foreground">
            Select a source to add from
          </p>
          <div className="flex items-center justify-center gap-8">
            <button
              onClick={() =>
                document.getElementById("ssp-import-file-input")?.click()
              }
              className="flex flex-col items-center gap-3 group"
            >
              <div className="size-20 rounded-full bg-muted flex items-center justify-center group-hover:bg-accent transition-colors">
                <Upload className="size-8 text-muted-foreground group-hover:text-foreground transition-colors" />
              </div>
              <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                Upload file
              </span>
            </button>
            <button
              onClick={() => setSspImportStep("url")}
              className="flex flex-col items-center gap-3 group"
            >
              <div className="size-20 rounded-full bg-muted flex items-center justify-center group-hover:bg-accent transition-colors">
                <Link2 className="size-8 text-muted-foreground group-hover:text-foreground transition-colors" />
              </div>
              <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                Source link
              </span>
            </button>
          </div>
          {sspImportFile && (
            <div
              className={`rounded-lg border w-full ${sspValidateResult
                ? "border-emerald-200 dark:border-emerald-500/20 bg-emerald-50 dark:bg-emerald-500/10"
                : sspImportError
                  ? "border-red-200 dark:border-red-500/20 bg-red-50 dark:bg-red-500/10"
                  : "border-border bg-muted"
                }`}
            >
              <div className="flex items-center gap-3 px-4 py-3">
                <FileText
                  className={`size-6 flex-shrink-0 ${sspValidateResult ? "text-emerald-500" : sspImportError ? "text-red-500" : "text-muted-foreground"}`}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {sspImportFile.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {(sspImportFile.size / 1024).toFixed(1)} KB
                  </p>
                  {sspValidating && (
                    <p className="text-xs text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5 mt-1">
                      <Loader2 className="size-3 animate-spin" /> Validating…
                    </p>
                  )}
                  {!sspValidating && sspValidateResult && (
                    <p className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5 mt-1">
                      <CheckCircle className="size-3" /> Validated -{" "}
                      {sspValidateResult.documentType}, OSCAL{" "}
                      {sspValidateResult.oscalVersion}
                    </p>
                  )}
                  {!sspValidating && sspImportError && (
                    <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1.5 mt-1">
                      <AlertTriangle className="size-3" /> {sspImportError}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setSspImportFile(null);
                    setSspValidateResult(null);
                    setSspImportError(null);
                    setSspValidating(false);
                  }}
                  className="p-1 rounded hover:bg-accent flex-shrink-0"
                >
                  <X className="size-4 text-muted-foreground" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step: URL input */}
      {sspImportStep === "url" && (
        <div className="space-y-4">
          <FormField
            label="Document URL"
            required
            hint="URL must end in .json, .yaml, or .xml"
          >
            <div className="flex gap-2">
              <div className="flex-1">
                <FormInput
                  placeholder="https://example.com/ssp.json"
                  value={sspImportUrl}
                  onChange={(v) => {
                    setSspImportUrl(v);
                    setSspValidateResult(null);
                    setSspImportError(null);
                  }}
                  disabled={sspValidating}
                />
              </div>
              <button
                onClick={sspFetchUrl}
                disabled={!sspImportUrl.trim() || sspValidating}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${!sspImportUrl.trim() || sspValidating
                  ? "bg-muted text-muted-foreground cursor-not-allowed"
                  : "bg-indigo-600 dark:bg-indigo-600 text-white hover:bg-indigo-700 dark:hover:bg-indigo-700"
                  }`}
              >
                {sspValidating ? (
                  <span className="flex items-center gap-1.5">
                    <Loader2 className="size-3.5 animate-spin" /> Fetching…
                  </span>
                ) : (
                  "Fetch"
                )}
              </button>
            </div>
          </FormField>
          {(sspValidating || sspValidateResult || sspImportError) && (
            <div
              className={`rounded-lg border w-full ${sspValidateResult
                ? "border-emerald-200 dark:border-emerald-500/20 bg-emerald-50 dark:bg-emerald-500/10"
                : sspImportError
                  ? "border-red-200 dark:border-red-500/20 bg-red-50 dark:bg-red-500/10"
                  : "border-border bg-muted"
                }`}
            >
              <div className="flex items-center gap-3 px-4 py-3">
                <Link2
                  className={`size-6 flex-shrink-0 ${sspValidateResult ? "text-emerald-500" : sspImportError ? "text-red-500" : "text-muted-foreground"}`}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {sspImportUrl.split("/").pop() || sspImportUrl}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {sspImportUrl}
                  </p>
                  {sspValidating && (
                    <p className="text-xs text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5 mt-1">
                      <Loader2 className="size-3 animate-spin" /> Fetching &
                      validating…
                    </p>
                  )}
                  {!sspValidating && sspValidateResult && (
                    <p className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5 mt-1">
                      <CheckCircle className="size-3" /> Validated -{" "}
                      {sspValidateResult.documentType}, OSCAL{" "}
                      {sspValidateResult.oscalVersion}
                    </p>
                  )}
                  {!sspValidating && sspImportError && (
                    <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1.5 mt-1">
                      <AlertTriangle className="size-3" /> {sspImportError}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setSspValidateResult(null);
                    setSspImportError(null);
                    setSspValidating(false);
                  }}
                  className="p-1 rounded hover:bg-accent flex-shrink-0"
                >
                  <X className="size-4 text-muted-foreground" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Importing spinner */}
      {sspImportStep === "importing" && (
        <div className="py-12 flex flex-col items-center gap-4">
          <Loader2 className="size-10 animate-spin text-indigo-500 dark:text-indigo-500" />
          <p className="text-sm font-medium">Importing document…</p>
          <p className="text-xs text-muted-foreground text-center">
            Uploading and processing your OSCAL SSP. This may take a moment
            for large files.
          </p>
        </div>
      )}

      {/* Error findings */}
      {sspImportStep === "findings" && (
        <div className="space-y-4">
          <div className="flex items-start gap-2 px-3 py-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm text-red-700 dark:text-red-400">
            <AlertTriangle className="size-4 mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <p className="text-xs">
                <span className="font-semibold">Error:</span>{" "}
                {sspImportErrorTitle || "Import Failed"}
              </p>
              <p className="text-xs">
                <span className="font-semibold">Message:</span>{" "}
                {sspImportError}
              </p>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
